import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useNavigate, useAppSelector, useAppDispatch } from '@/hooks';
import { renderComponent } from '@/components/componentRegistry';
import { getLoanOfficerConfig } from './config/loanOfficerConfig';
import { ColumnLayout } from '@/components/atoms';
import { useLoan } from '@/contexts';
import type { MappedLoanOfficersResponse } from '@/services/mappers/loanOfficer.mapper';
import type { MappedBranchesResponse } from '@/services/mappers/branch.mapper';
import { loanOfficerService, loanOfficerMapper } from '@/services';
import {
  setLoanOfficers,
  setLoanOfficersLoading,
} from '@/store/slices/loanOfficers.slice';

/**
 * LoanOfficer - Loan Officer Selection Page
 * This is the first page in the QuickLoan flow
 * User selects a loan officer before proceeding to the landing page
 *
 * API call is made on mount using config-driven api:{} pattern
 * Data is stored in Redux and consumed from there
 */
export const LoanOfficer: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { updateApplicationData, applicationData } = useLoan();

  // Get branchId from query params
  const branchIdFromParams = searchParams.get('branchId');

  // Get loan officers from Redux
  const { officers: loanOfficers, isLoading: isLoadingOfficers } =
    useAppSelector((state) => state.loanOfficers);

  const [selectedOfficer, setSelectedOfficer] = useState<string>(
    (applicationData.loanOfficer as string) || ''
  );

  // Handler for API data loaded callback - stores loan officers in Redux
  const handleLoanOfficersLoaded = (
    response: MappedLoanOfficersResponse | null
  ) => {
    if (response) {
      dispatch(
        setLoanOfficers({
          officers: response.officers,
          total: response.total,
        })
      );
    }
  };

  // Handler for branch data loaded callback - stores in context for persistence
  // Always store fresh data from API to ensure complete info for modal
  const handleBranchDataLoaded = (response: MappedBranchesResponse | null) => {
    if (response && response.branches.length > 0) {
      updateApplicationData({
        branchData: response.branches[0],
      });
      console.log(
        'Branch data from API stored in context:',
        response.branches[0]
      );
    }
  };

  // Handler for loading state
  const handleLoader = (loading: boolean) => {
    dispatch(setLoanOfficersLoading(loading));
  };

  const handleSearchChange = (_searchTerm: string) => {
    // Optionally filter locally if needed
  };

  const handleOfficerSelect = async (officerId: string) => {
    setSelectedOfficer(officerId);
    const officer = loanOfficers.find((o) => o.id === officerId);
    if (officer) {
      // First, update with the basic data from the list
      updateApplicationData({
        loanOfficer: officerId,
        loanOfficerId: officerId,
        loanOfficerNmlsId: officer.nmls || '',
        loanOfficerName: officer.name || '',
        loanOfficerPhoto: officer.photo || '',
        loanOfficerData: officer,
      });

      // Then fetch full LO data by NMLS to ensure we have complete info for the contact modal
      if (officer.nmls) {
        try {
          const fullOfficerData =
            await loanOfficerService.getLoanOfficerByIdentifier({
              nmlsId: officer.nmls,
            });
          const mappedFullData =
            loanOfficerMapper.mapLoanOfficerToComponentFormat(fullOfficerData);
          // Update with full data (includes address, website, etc.)
          updateApplicationData({
            loanOfficerData: mappedFullData,
          });
        } catch (error) {
          console.error('Error fetching full loan officer data:', error);
          // Keep using the list data if the API call fails
        }
      }
    }
  };

  const handleNext = () => {
    if (selectedOfficer) {
      navigate('/quickloan/landing-loan-officer');
    }
  };

  const handleNoOfficer = () => {
    // Clear loan officer data, keep branch data from context (already stored on mount)
    updateApplicationData({
      loanOfficer: undefined,
      loanOfficerId: undefined,
      loanOfficerNmlsId: undefined,
      loanOfficerName: undefined,
      loanOfficerData: undefined,
      // branchData is already stored in context from mount, no need to set again
    });
    navigate('/quickloan/setup');
  };

  const config = getLoanOfficerConfig({
    theme,
    t,
    onOfficerSelect: handleOfficerSelect,
    selectedOfficer,
    onNext: handleNext,
    onNoOfficer: handleNoOfficer,
    loanOfficers,
    onSearchChange: handleSearchChange,
    isLoadingOfficers,
    hasOfficerInContext: !!applicationData.loanOfficerData,
    selectedOfficerData: applicationData.loanOfficerData,
    onLoanOfficersLoaded: handleLoanOfficersLoaded,
    onBranchDataLoaded: handleBranchDataLoaded,
    branchId: branchIdFromParams || '1040',
    handleLoader,
  });

  return (
    <ColumnLayout spacing="none" fullWidth fullHeight>
      {renderComponent(config, 0, false)}
    </ColumnLayout>
  );
};

export default LoanOfficer;
